import { useState, useEffect, useMemo } from 'react';
import { X, Calendar, Clock, User, Phone, Mail, RefreshCw, CheckCircle, XCircle, Loader2, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { formatearFechaCortaCaracas, formatearHoraCaracas } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ModalControlAsistencia({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [ausentes, setAusentes] = useState([]);
  const [atendidos, setAtendidos] = useState([]);
  const [reagendando, setReagendando] = useState(false);
  const [expedienteReagendar, setExpedienteReagendar] = useState(null);
  const [nuevaFecha, setNuevaFecha] = useState('');
  const [nuevaHora, setNuevaHora] = useState('');
  const [searchCedula, setSearchCedula] = useState('');

  const normalizeCedula = (c) => c.replace(/[.\-,VvEe\s]/g, '');

  const filteredAusentes = useMemo(() => {
    if (!searchCedula.trim()) return ausentes;
    const q = normalizeCedula(searchCedula);
    return ausentes.filter(exp => normalizeCedula(exp.solicitante?.cedula || '').includes(q));
  }, [ausentes, searchCedula]);

  const filteredAtendidos = useMemo(() => {
    if (!searchCedula.trim()) return atendidos;
    const q = normalizeCedula(searchCedula);
    return atendidos.filter(exp => normalizeCedula(exp.solicitante?.cedula || '').includes(q));
  }, [atendidos, searchCedula]);

  useEffect(() => {
    if (isOpen) {
      loadCitasAsistencia();
    }
  }, [isOpen]);

  const loadCitasAsistencia = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/gerencial/citas-asistencia`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAusentes(data.ausentes || []);
        setAtendidos(data.atendidos || []);
      } else {
        toast.error('Error al cargar citas');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleReagendarClick = (expediente) => {
    setExpedienteReagendar(expediente);
    // Sugerir fecha/hora: mañana a las 10:00
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setNuevaFecha(tomorrow.toISOString().split('T')[0]);
    setNuevaHora('10:00');
  };

  const handleReagendarConfirm = async () => {
    if (!nuevaFecha || !nuevaHora) {
      toast.error('Selecciona fecha y hora');
      return;
    }

    setReagendando(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/gerencial/reagendar-cita-ausente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          expediente_id: expedienteReagendar.expediente_id,
          fecha: nuevaFecha,
          hora: nuevaHora
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Cita reagendada exitosamente');
        setExpedienteReagendar(null);
        loadCitasAsistencia(); // Recargar lista
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al reagendar');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al reagendar');
    } finally {
      setReagendando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden border border-gray-700">
        {/* Header */}
        <div className="bg-gray-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Calendar className="w-6 h-6 text-yellow-500" />
              Control de Asistencia
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Gestión de citas pasadas y reagendamiento
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            data-testid="close-asistencia-modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Buscador por cédula */}
        <div className="px-6 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por cedula..."
              value={searchCedula}
              onChange={(e) => setSearchCedula(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:border-yellow-500 placeholder-gray-500 text-sm"
              data-testid="search-cedula-asistencia"
            />
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-12 h-12 text-yellow-500 animate-spin" />
            </div>
          ) : (
            <Tabs defaultValue="ausentes" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-800">
                <TabsTrigger value="ausentes" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
                  Ausentes / Por Reagendar ({filteredAusentes.length})
                </TabsTrigger>
                <TabsTrigger value="atendidos" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                  Atendidos Historico ({filteredAtendidos.length})
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Ausentes */}
              <TabsContent value="ausentes" className="mt-6">
                {filteredAusentes.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">
                      {searchCedula ? 'No se encontraron resultados' : 'No hay clientes ausentes'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredAusentes.map((exp) => (
                      <Card key={exp.expediente_id} className="bg-gray-800/50 border-red-500/30">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-white font-semibold">{exp.solicitante.nombre_completo}</h3>
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                  Ausente
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3 text-sm text-gray-400">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4" />
                                  {exp.solicitante.cedula}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Mail className="w-4 h-4" />
                                  {exp.solicitante.email}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4" />
                                  {exp.solicitante.telefono || 'No disponible'}
                                </div>
                                <div className="flex items-center gap-2 text-red-400">
                                  <Calendar className="w-4 h-4" />
                                  {formatearFechaCortaCaracas(exp.cita.fecha)} - {formatearHoraCaracas(exp.cita.fecha)}
                                </div>
                              </div>
                              
                              <p className="text-xs text-gray-500 mt-2">
                                Expediente: {exp.numero_expediente}
                              </p>
                            </div>

                            <Button
                              onClick={() => handleReagendarClick(exp)}
                              className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Reagendar
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Tab 2: Atendidos */}
              <TabsContent value="atendidos" className="mt-6">
                {filteredAtendidos.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-400">{searchCedula ? 'No se encontraron resultados' : 'No hay citas atendidas en el historial'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredAtendidos.map((exp) => (
                      <Card key={exp.expediente_id} className="bg-gray-800/50 border-green-500/30">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-white font-semibold">{exp.solicitante.nombre_completo}</h3>
                                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                  Atendido
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3 text-sm text-gray-400">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4" />
                                  {exp.solicitante.cedula}
                                </div>
                                <div className="flex items-center gap-2 text-green-400">
                                  <Calendar className="w-4 h-4" />
                                  {formatearFechaCortaCaracas(exp.cita.fecha)} - {formatearHoraCaracas(exp.cita.fecha)}
                                </div>
                              </div>
                              
                              <p className="text-xs text-gray-500 mt-2">
                                Expediente: {exp.numero_expediente} | Estado: {exp.estado}
                              </p>
                            </div>

                            <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Completado
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* Footer con estadísticas */}
        <div className="bg-gray-900 border-t border-gray-700 p-4 flex justify-between items-center">
          <div className="flex gap-6 text-sm">
            <span className="text-red-400">
              Ausentes: <strong>{filteredAusentes.length}</strong>
            </span>
            <span className="text-green-400">
              Atendidos: <strong>{filteredAtendidos.length}</strong>
            </span>
          </div>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Cerrar
          </Button>
        </div>
      </div>

      {/* Sub-Modal para Reagendar */}
      {expedienteReagendar && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]">
          <Card className="bg-gray-900 border-gray-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-yellow-500" />
                Reagendar Cita
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-300 text-sm mb-2">
                  <strong>Cliente Ausente:</strong>
                </p>
                <p className="text-white">{expedienteReagendar.solicitante.nombre_completo}</p>
                <p className="text-gray-400 text-sm">Cédula: {expedienteReagendar.solicitante.cedula}</p>
                <p className="text-red-400 text-sm mt-2">
                  Cita perdida: {formatearFechaCortaCaracas(expedienteReagendar.cita.fecha)} - {formatearHoraCaracas(expedienteReagendar.cita.fecha)}
                </p>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Nueva Fecha *
                </label>
                <input
                  type="date"
                  value={nuevaFecha}
                  onChange={(e) => setNuevaFecha(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:border-yellow-500"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Nueva Hora *
                </label>
                <select
                  value={nuevaHora}
                  onChange={(e) => setNuevaHora(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:border-yellow-500"
                >
                  <option value="">Seleccionar hora...</option>
                  {/* Horario 9:00 AM - 12:00 PM */}
                  <option value="09:00">09:00 AM</option>
                  <option value="09:30">09:30 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="10:30">10:30 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="11:30">11:30 AM</option>
                  {/* Horario 2:00 PM - 5:00 PM */}
                  <option value="14:00">02:00 PM</option>
                  <option value="14:30">02:30 PM</option>
                  <option value="15:00">03:00 PM</option>
                  <option value="15:30">03:30 PM</option>
                  <option value="16:00">04:00 PM</option>
                  <option value="16:30">04:30 PM</option>
                </select>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  📧 Se enviará automáticamente un email de confirmación con la nueva fecha al cliente.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setExpedienteReagendar(null);
                    setNuevaFecha('');
                    setNuevaHora('');
                  }}
                  className="flex-1 border-gray-600 text-gray-300"
                  disabled={reagendando}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleReagendarConfirm}
                  disabled={reagendando || !nuevaFecha || !nuevaHora}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                >
                  {reagendando ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Reagendando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirmar Reagendamiento
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
